import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { Metric } from '@atlassian/browser-metrics/metric';
import { isCreatableAction } from '@atlassian/conversation-assistant-action-helpers/utils';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import type { ExperienceTrackerAPI } from '@atlassian/conversation-assistant-instrumentation/main';
import { ASK_QUESTION_KEY } from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type MinionOutputs,
	type NoCreateClassification,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type Conversation,
	isStoreAssistant,
	isStoreHuman,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { extractOutboundAuthLink } from '@atlassian/smart-creation/auth-link';

import { abortPreviewCardRenderExperience } from '../../common/utils/abort-preview-card-render-experience';
import { abortGenerateContentChildExperiences } from '../../common/utils/aifc-experience-collect/abort-generate-content-child-experiences';
import { createStoreError } from './createStoreError';
import { NoActionInvokedError } from './error-classes/NoActionInvokedError';
import type { ChatStoreContainerProps } from './types';

// Only these classifications get the softer "no content" hint screen.
// Everything else (including missing or unknown) falls through to the error screen.
const ABORT_CLASSIFICATIONS: Set<NoCreateClassification> = new Set([
	'BAD_USER_PROMPT',
	'GOOD_REPLY_NO_CREATE',
	'EXTERNAL_ERROR',
]);

export const handleStagingAreaActionValidation = (
	allMessages: Conversation['messages'],
	conversationId: string,
	traceId: string | undefined,
	chatContextStoreActions: ChatStoreContainerProps['chatContextStoreActions'],
	experienceTracker: ExperienceTrackerAPI,
	getMessages: ChatStoreContainerProps['getMessages'],
	setMessages: ChatStoreContainerProps['setMessages'],
	sendMessageLatencyMetrics: Metric,
	cancelStreamingMetrics: () => void,
	commonExperienceAttributes: Record<string, unknown>,
	productKey: string | undefined,
	minionOutputs?: MinionOutputs,
	experience?: string,
): boolean => {
	// Some products do not require first response to be an action,
	// so exit early when we're not within Confluence
	if (productKey !== 'confluence') {
		return true;
	}

	// chat_edit is a global chat experience editing an existing page — not a CWR create flow.
	// A creatable action is not expected, so skip this validation entirely.
	if (fg('confluence_aifc_new_live_page_chat_context') && experience === 'chat_edit') {
		return true;
	}

	const humanMessageCount = allMessages.filter(isStoreHuman).length;

	// Only execute on the first human message within the staging area
	if (chatContextStoreActions.getIsStagingArea() && humanMessageCount === 1) {
		const firstAssistantResponse = allMessages.find(isStoreAssistant);
		const firstAction = firstAssistantResponse?.content?.message?.message?.actions?.[0];

		const hasCreatableAction = firstAction ? isCreatableAction(firstAction) : false;

		// An ask_question action is a valid first response (the agent is asking the user a
		// clarifying/disambiguation question), not a failed content creation. Treat it as
		// valid so the FINAL_RESPONSE is not misclassified as "no content created".
		const isAskQuestionAction = firstAction?.key === ASK_QUESTION_KEY;

		const authUrl = extractOutboundAuthLink(
			firstAssistantResponse?.content?.message?.message?.content ?? '',
		);

		// this experience has a timeout. since ask_question waits for the user, we cannot
		// bound the time. so we abort it now.
		// AIFC_GENERATE_CONTENT and its other sub experiences are intentionally left open.
		// content creation is still what we're waiting for, it just happens on a later turn.
		if (isAskQuestionAction) {
			const reason = 'ask_question - awaiting user response, cannot bound time';
			experienceTracker.abort({
				name: ExperienceName.AIFC_INITIAL_DATA_RECEIVED,
				reason,
				attributes: commonExperienceAttributes,
			});
			abortPreviewCardRenderExperience({
				experienceTracker,
				reason,
				terminalReason: 'awaiting-disambiguation',
				attributes: commonExperienceAttributes,
			});
		}

		// Experience handling for the scenario where there
		// should be an action returned but there was not
		if (!hasCreatableAction && !isAskQuestionAction) {
			// If authUrl is present, abort the experience
			if (authUrl) {
				experienceTracker.abort({
					name: ExperienceName.AIFC_GENERATE_CONTENT,
					reason: 'authentication-required',
					attributes: commonExperienceAttributes,
				});

				abortGenerateContentChildExperiences(
					experienceTracker,
					'AIFC_GENERATE_CONTENT aborted - authentication required',
					commonExperienceAttributes,
				);
			} else {
				// This error should have a user-friendly message, as it will be shown to the user.
				const noActionError = new NoActionInvokedError(
					"Something went wrong on Rovo's end. Please try sending your message again.",
					{
						traceId: traceId,
					},
				);

				const hasConfluenceCreationTool = minionOutputs
					?.flat()
					.some((output) => output.minion_id === 'ConfluenceCreationTool');

				const messageMetadata = firstAssistantResponse?.content?.message?.message?.message_metadata;
				const orchestratorDepthLimitReached =
					messageMetadata?.orchestrator_depth_limit_reached ?? false;
				const orchestratorContextWindowUsage = messageMetadata?.orchestrator_context_window_usage;

				const classification = messageMetadata?.aifc_no_create_classification;

				const noContentCreatedAttributes = {
					orchestratorContextWindowUsage,
					orchestratorDepthLimitReached,
					hasConfluenceCreationTool,
					noContentCreatedClassification: classification ?? 'NO_CLASSIFICATION_PROVIDED',
				};

				if (!classification || !ABORT_CLASSIFICATIONS.has(classification)) {
					experienceTracker.fail({
						name: ExperienceName.AIFC_GENERATE_CONTENT,
						error: noActionError,
						attributes: {
							...commonExperienceAttributes,
							...noContentCreatedAttributes,
						},
					});

					abortGenerateContentChildExperiences(
						experienceTracker,
						classification ?? 'NO_CLASSIFICATION_PROVIDED',
						commonExperienceAttributes,
					);

					const messages = getMessages(conversationId);
					setMessages(conversationId, [...messages, createStoreError(noActionError)]);

					sendMessageLatencyMetrics.cancel();
					cancelStreamingMetrics();

					return false;
				}

				// Abort: user prompt or external issue — nudge user to fix the problem
				experienceTracker.abort({
					name: ExperienceName.AIFC_GENERATE_CONTENT,
					reason: classification,
					attributes: {
						...commonExperienceAttributes,
						...noContentCreatedAttributes,
					},
				});

				abortGenerateContentChildExperiences(
					experienceTracker,
					classification,
					commonExperienceAttributes,
				);

				// GOOD_REPLY_NO_CREATE is symptomatic of the user prompting non-creation, which we can also treat as a bad prompt
				const hintVariant =
					classification === 'EXTERNAL_ERROR' ? 'EXTERNAL_ERROR' : 'BAD_USER_INPUT';

				const messages = getMessages(conversationId);
				setMessages(conversationId, [
					...messages,
					{
						type: 'USAGE_HINT',
						topic: 'NoActionInvokedError',
						variant: hintVariant,
					},
				]);

				return true;
			}
		}
	}
	return true;
};
